import { useEffect, useMemo, useRef, type ReactNode } from 'react'

/**
 * An email's body. HTML renders in a sandboxed, script-free iframe sized to its
 * content (the SDK has already sanitized it and marked trackers and quoted
 * history); plain text renders inline with links.
 */
/** Last measured height per email body, so a reopened email has its final size on the first frame. */
const heights = new Map<string, number>()

export function MessageBody({ html, text, format, styles = '', onKey }: { html: string; text: string; format?: 'html' | 'text'; styles?: string; onKey?: (event: KeyboardEvent) => void }) {
  const plain = format === 'text' || !html.trim()
  const doc = useMemo(() => plain ? null : emailDocument(html, styles), [plain, html, styles])
  const frame = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const element = frame.current
    if (!element || !doc) return
    let observer: ResizeObserver | null = null
    let raf = 0
    const fit = () => {
      const d = element.contentDocument
      if (!d?.body) return
      const root = d.documentElement
      root.style.removeProperty('zoom')
      element.style.height = '1px'
      const width = element.clientWidth
      const contentWidth = Math.max(d.body.scrollWidth, root.scrollWidth, width)
      const scale = Math.min(1, width / contentWidth)
      if (scale < 1) root.style.setProperty('zoom', String(scale), 'important')
      const height = Math.ceil(Math.max(d.body.scrollHeight * scale, root.scrollHeight, 40))
      element.style.height = `${height}px`
      element.classList.add('fitted')
      heights.set(html, height)
      if (heights.size > 200) heights.delete(heights.keys().next().value!)
    }
    const attach = () => {
      const d = element.contentDocument
      if (!d?.body || d.documentElement.dataset.mm !== doc.id) { raf = requestAnimationFrame(attach); return }
      fit()
      observer = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(fit) })
      observer.observe(d.body)
      d.addEventListener('load', fit, true)
      d.addEventListener('toggle', fit, true)
      // Images that can't load disappear instead of showing a broken icon and alt text.
      const hide = (image: HTMLImageElement) => { image.style.setProperty('visibility', 'hidden', 'important'); fit() }
      d.addEventListener('error', event => { const t = event.target as HTMLElement; if (t.tagName === 'IMG') hide(t as HTMLImageElement) }, true)
      for (const image of Array.from(d.images)) if (image.complete && !image.naturalWidth && image.getAttribute('src')) hide(image)
      if (onKey) d.addEventListener('keydown', onKey)
    }
    raf = requestAnimationFrame(attach)
    return () => { cancelAnimationFrame(raf); observer?.disconnect() }
  }, [doc, onKey, html])

  if (plain) return <div className="body-text" dir="auto">{linked(text || '(This message has no text.)')}</div>
  return (
    <div className="body-html">
      <iframe ref={frame} title="Email content" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" srcDoc={doc!.html} style={{ height: heights.get(html) ?? 160 }} />
    </div>
  )
}

function emailDocument(html: string, styles: string) {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const id = crypto.randomUUID()
  parsed.documentElement.dataset.mm = id
  const head = parsed.createElement('head')
  head.innerHTML = `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: http: data:; font-src https: data:; form-action 'none'; base-uri 'none'"><meta name="referrer" content="no-referrer"><base target="_blank">`
  const defaults = parsed.createElement('style')
  defaults.textContent = `
    :where(html) { color-scheme: light; color: #1a1a1a; background: #fff; font: 15px/1.6 -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif; -webkit-font-smoothing: antialiased; }
    :where(body) { margin: 0; padding: 20px 24px; overflow-wrap: anywhere; }
    :where(img, table) { max-width: 100%; }
    :where(img) { height: auto; }
    :where(pre) { white-space: pre-wrap; }
    :where(a) { color: #0066d6; }
    html { overflow-y: hidden; }
    /* Many emails ask for "Inter"; a partial local install can resolve to its italic face. Map it to the system font. */
    @font-face { font-family: "Inter"; font-style: normal; font-weight: 100 600; src: local("Helvetica Neue"), local("HelveticaNeue"), local("Arial"); }
    @font-face { font-family: "Inter"; font-style: normal; font-weight: 700 900; src: local("Helvetica Neue Bold"), local("HelveticaNeue-Bold"), local("Arial Bold"); }
    @font-face { font-family: "Inter"; font-style: italic; src: local("Helvetica Neue Italic"), local("HelveticaNeue-Italic"), local("Arial Italic"); }
    details[data-mm] > summary { cursor: pointer; color: #6e6e6e; font: 13px/1.6 -apple-system, sans-serif; padding: 6px 0; }
  `
  const author = parsed.createElement('style')
  author.textContent = styles.replace(/<\/style/gi, '<\\/style')
  head.append(defaults, author)
  parsed.head.replaceWith(head)
  // Fold the SDK-marked quoted history behind a disclosure.
  for (const first of parsed.querySelectorAll('[data-inbox-quoted-history="true"]')) {
    if (first.closest('details[data-mm]')) continue
    const disclosure = parsed.createElement('details')
    disclosure.dataset.mm = '1'
    const summary = parsed.createElement('summary')
    summary.textContent = '···'
    disclosure.append(summary)
    first.before(disclosure)
    let node: ChildNode | null = first
    while (node && (node.nodeType === Node.COMMENT_NODE || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) || (node instanceof Element && node.getAttribute('data-inbox-quoted-history') === 'true'))) {
      const next: ChildNode | null = node.nextSibling
      disclosure.append(node)
      node = next
    }
  }
  for (const image of parsed.querySelectorAll('img[data-inbox-tracking="true"]')) image.remove()
  // The SDK proxies remote images at /v1/…, relative to its API; ours is mounted under /api/inbox.
  for (const image of parsed.querySelectorAll<HTMLImageElement>('img[src^="/v1/"]')) image.setAttribute('src', `/api/inbox${image.getAttribute('src')}`)
  for (const image of parsed.querySelectorAll<HTMLImageElement>('img[data-openmail-src]:not([src])')) image.style.setProperty('opacity', '0', 'important')
  return { id, html: `<!doctype html>${parsed.documentElement.outerHTML}` }
}

/** Plain text with links and quoted replies folded. */
function linked(text: string): ReactNode[] {
  const cleaned = text.replace(/\r\n/g, '\n')
  // Hide everything from the first "On … wrote:" or "> " block onward; it's the quoted history.
  const quote = cleaned.search(/\n(On .{5,200}wrote:\n|-{2,} ?Original Message ?-{2,}|>[^\n]*\n>)/)
  const shown = quote > 40 ? cleaned.slice(0, quote).trimEnd() : cleaned
  const parts: ReactNode[] = []
  const pattern = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/g
  let last = 0
  for (const match of shown.matchAll(pattern)) {
    parts.push(shown.slice(last, match.index))
    parts.push(<a key={match.index} href={match[0]} target="_blank" rel="noopener noreferrer">{match[0].length > 60 ? `${match[0].slice(0, 57)}…` : match[0]}</a>)
    last = match.index! + match[0].length
  }
  parts.push(shown.slice(last))
  if (shown !== cleaned) parts.push(<details key="q" className="quoted"><summary>···</summary>{cleaned.slice(shown.length)}</details>)
  return parts
}
